import React from 'react'
import { Typography, Avatar, Box, SvgIcon } from '@mui/material'
import { t } from 'i18next'
import { differenceInDays, isValid } from 'date-fns'
import RetentionBadges from './components/client-header/retention-badges.component'

import { AlertIconsRow } from '@hapana/shared/web/components/shared/alerts-system'
import {
  SORTING_DIRECTIONS,
  TABLE_SKELETON_SHAPES,
} from '@hapana/shared/web/components/shared/table/table.constants'
import { formatSiteDate } from '@hapana/shared/utils/date.util'
import BillIcon from '@hapana/shared/web/styles/assets/svgs/bill.svg'
import PackageIcon from '@hapana/shared/web/styles/assets/svgs/package-small.svg'
import FileIcon from '@hapana/shared/web/styles/assets/svgs/file-alt-small.svg'
import InfoIcon from '@hapana/shared/web/styles/assets/svgs/info.svg'
import EnvelopeIcon from '@hapana/shared/web/styles/assets/svgs/envelope-small.svg'
import UserIcon from '@hapana/shared/web/styles/assets/svgs/user-small.svg'
import { getAvatarColor } from '../../utils/avatar-color.util'

import { clientsTableStyles, getAvatarStyle } from './clients.styles'

import {
  CLIENT_ACTIVE_STATUSES,
  CLIENT_SLEEPING_ELIGIBLE_STATUSES,
} from '@hapana/shared/schemas/users/client-schemas'

export {
  CLIENT_ACTIVE_STATUSES as ACTIVE_PAYING_STATUSES,
  CLIENT_SLEEPING_ELIGIBLE_STATUSES as SLEEPING_ELIGIBLE_STATUSES,
}

export const computeClientDaysAbsent = (client) => {
  if (client.isSuspended) {
    return null
  }
  const isEligible = CLIENT_SLEEPING_ELIGIBLE_STATUSES.includes(client.membershipStatus ?? '')
  if (!isEligible) {
    return null
  }
  const raw = client.lastVisit ?? client.joinDate
  if (!raw) {
    return null
  }
  const parsed = /^\d+$/.test(String(raw)) ? Number(raw) : raw
  const lastDate = new Date(parsed)
  if (!isValid(lastDate)) {
    return null
  }
  return differenceInDays(new Date(), lastDate)
}

export const ALL_CLIENTS_TAB = 'all'
export const DEFAULT_COUNTRY_CODE = 'US'
export const INITIAL_CURSOR_SENTINEL = 'initial'
export const GRAPHQL_TYPENAME_KEY = '__typename'
export const OVERVIEW_TAB = 'OVERVIEW'

export const STORAGE_KEYS = {
  KEEP_FILTERS_CHECKED: 'keepFiltersChecked',
  SHOW_PROSPECTS: 'clientsShowProspects',
}

export const PAGE_NAME = 'clients'
export const PACKAGES_TAB_PARAM = 'packages'

export const CSS_CLASS_NAMES = {
  SELECTED: 'selected',
  CLIENT_NAME: 'client-name',
  CLIENT_EMAIL: 'client-email',
  PHONE_VALUE: 'phone-value',
  BARCODE_VALUE: 'barcode-value',
  TABLE_WRAPPER: 'clients-table-wrapper',
}

const statusLabelMap = {
  deleted: 'clients.filter.status.deleted',
  blocked: 'clients.filter.status.blocked',
  prospect: 'clients.filter.status.prospect',
  suspended: 'clients.filter.status.suspended',
  membership: 'clients.filter.status.membership',
  package: 'clients.filter.status.package',
  intro: 'clients.filter.status.intro',
  pending: 'clients.filter.status.pending',
  alumni: 'clients.filter.status.alumni',
  aggregator: 'clients.filter.status.aggregator',
  lead: 'clients.filter.status.lead',
}

export const getStatusLabel = (status, t) => {
  const key = statusLabelMap[status]
  return key ? t(key) : status
}

const statusTooltipMap = {
  prospect: 'clients.retentionBadges.statusTooltip.prospect',
  suspended: 'clients.retentionBadges.statusTooltip.suspended',
  membership: 'clients.retentionBadges.statusTooltip.membership',
  package: 'clients.retentionBadges.statusTooltip.package',
  intro: 'clients.retentionBadges.statusTooltip.intro',
  pending: 'clients.retentionBadges.statusTooltip.pending',
  alumni: 'clients.retentionBadges.statusTooltip.alumni',
  aggregator: 'clients.retentionBadges.statusTooltip.aggregator',
  lead: 'clients.retentionBadges.statusTooltip.lead',
}

export const getStatusTooltip = (status, t) => {
  const key = statusTooltipMap[status]
  return key ? t(key) : ''
}

const TAB_PARAM_OVERRIDES = {
  INFORMATION: 'client-info',
  PAYMENT: 'payments',
  CONTACT_LOGS: 'contact-logs',
  SOAP_SESSION: 'soap-session',
}

export const getDefaultClientTabParam = (defaultTab) => {
  if (!defaultTab || defaultTab === OVERVIEW_TAB) {
    return ''
  }

  const param = TAB_PARAM_OVERRIDES[defaultTab] || defaultTab.toLowerCase()
  return `?tab=${param}`
}

export const transformAlertsToSystemItems = (alerts = []) => {
  if (!Array.isArray(alerts)) {
    return []
  }

  return alerts
    .map((alert, idx) => {
      const alertType = alert?.type
      if (!alertType) {
        return null
      }

      // `firstSession` is a scheduling concept — it only makes sense against a specific
      // session in the Schedule (session details / waitlist), where it warns operators
      // that this is the client's very first booked session. Every surface that consumes
      // this clients-domain transform (the Clients list, the new-sale cart, etc.) is
      // non-scheduling, so it must never surface `firstSession`. The Schedule has its own
      // local transforms and opts in via `ClientAlertsRow`'s `includeFirstSession`.
      if (alertType === 'firstSession') {
        return null
      }

      return {
        clientId: alert?.clientId || `unknown-client-${idx}`,
        type: alertType,
        data: alert?.data || undefined,
        requiresFetch: alert?.requiresFetch || false,
      }
    })
    .filter(Boolean)
}

export const ALERT_TYPES = {
  PAYMENT: { icon: BillIcon, color: 'orange.lightest', iconColor: 'orange.dark' },
  MEMBERSHIP: { icon: PackageIcon, color: 'success.lightest', iconColor: 'success.dark' },
  COMMUNICATION: { icon: UserIcon, color: 'secondary.lightest', iconColor: 'secondary.dark' },
  EMAIL: { icon: EnvelopeIcon, color: 'blue.lightest', iconColor: 'blue.dark' },
  GENERAL: { icon: InfoIcon, color: 'orange.lightest', iconColor: 'orange.dark' },
  DOCUMENT: { icon: FileIcon, color: 'pink.lightest', iconColor: 'pink.dark' },
}

export const CLIENTS_TABLE_DATA_STRUCTURE = {
  id: {
    key: 'id',
    label: t('core:clients.table.columns.id'),
    serverSideKey: 'id',
  },
  name: {
    label: t('core:clients.table.columns.name'),
    key: 'name',
    serverSideKey: 'name',
  },
  alerts: {
    label: t('core:clients.table.columns.alerts'),
    key: 'alerts',
    serverSideKey: 'alerts',
  },
  phone: {
    label: t('core:clients.table.columns.phone'),
    key: 'mobilePhoneNumber',
    serverSideKey: 'mobilePhoneNumber',
  },
  barcode: {
    label: t('core:clients.table.columns.barcode'),
    key: 'barcode',
    serverSideKey: 'barcode',
  },
  status: {
    label: t('core:clients.table.columns.status'),
    key: 'membershipStatus',
    serverSideKey: 'membershipStatus',
  },
  createdAt: {
    label: t('core:clients.table.columns.createdAt'),
    key: 'createdAt',
    serverSideKey: 'createdAt',
  },
}

export const getClientsTableColumns = (
  onClientClick,
  selectedRows = new Set(),
  t = (key) => key,
  buildClientHref = () => undefined,
  siteTimezone
) => [
  {
    Header: CLIENTS_TABLE_DATA_STRUCTURE.name.label,
    accessor: CLIENTS_TABLE_DATA_STRUCTURE.name.key,
    customRowRender: (_, cell) => {
      const client = cell.row.original
      const initials = `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`
      const isSelected = selectedRows.has(client.id)

      return (
        <Box
          component="a"
          href={buildClientHref(client.id)}
          data-testid={`client-row-${client.id}`}
          sx={clientsTableStyles.clientName.container}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
              return
            }
            e.preventDefault()
            onClientClick(client.id)
          }}
        >
          <Avatar
            src={
              client.profileImage?.({ input: { type: 'thumbnail', format: 'webp' } })?.url ??
              undefined
            }
            sx={getAvatarStyle(
              client.id || client.clientId,
              clientsTableStyles.clientName.avatar,
              getAvatarColor
            )}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography
              className={CSS_CLASS_NAMES.CLIENT_NAME}
              typography="header4"
              sx={
                isSelected
                  ? clientsTableStyles.clientName.nameSelected
                  : clientsTableStyles.clientName.name
              }
            >
              {client.name}
            </Typography>
            <Typography
              typography="small"
              sx={
                isSelected
                  ? clientsTableStyles.clientName.emailSelected
                  : clientsTableStyles.clientName.email
              }
            >
              {client.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    hasSorting: true,
    serverSideKey: CLIENTS_TABLE_DATA_STRUCTURE.name.serverSideKey,
    skeletonShape: TABLE_SKELETON_SHAPES.avatarText,
    width: '250px',
  },
  {
    Header: CLIENTS_TABLE_DATA_STRUCTURE.alerts.label,
    accessor: CLIENTS_TABLE_DATA_STRUCTURE.alerts.key,
    customRowRender: (_, cell) => {
      const client = cell.row.original

      const alerts = client?.alerts || cell.value || []
      const alertItems = transformAlertsToSystemItems(alerts)

      // OC-782: days-absent alert hidden from the clients list. The metric counts class
      // attendance only, so members who attend via general check-in still show a large
      // "days absent". To restore, re-add the getDaysAbsentBadge import and the badge
      // below once the metric accounts for general check-ins.
      // const daysAbsent = computeClientDaysAbsent(client)
      // const daysAbsentBadge = getDaysAbsentBadge(daysAbsent)
      const allItems = [
        ...alertItems,
        // ...(daysAbsentBadge && daysAbsent != null && daysAbsent >= 7
        //   ? [
        //       {
        //         clientId: client?.id || client?.clientId || '',
        //         type: 'daysAbsent',
        //         data: { daysAbsent },
        //         colorsOverride: { bg: daysAbsentBadge.bgcolor, icon: daysAbsentBadge.color },
        //       },
        //     ]
        //   : []),
      ]

      return (
        <AlertIconsRow
          items={allItems}
          disableDrawer
          clientContext={{
            id: client?.id || client?.clientId || '',
            name: client?.name || '',
            firstName: client?.firstName || '',
            profileImageUrl:
              client?.profileImage?.({ input: { type: 'thumbnail', format: 'webp' } })?.url ??
              undefined,
          }}
        />
      )
    },
    width: 'auto',
  },
  {
    Header: CLIENTS_TABLE_DATA_STRUCTURE.phone.label,
    accessor: CLIENTS_TABLE_DATA_STRUCTURE.phone.key,
    customRowRender: (_, cell) => {
      const client = cell.row.original
      const isSelected = selectedRows.has(client.id)

      return (
        <Box>
          <Typography
            typography="medium"
            className={CSS_CLASS_NAMES.PHONE_VALUE}
            sx={
              isSelected
                ? clientsTableStyles.phone.typographySelected
                : clientsTableStyles.phone.typography
            }
          >
            {cell.value || '—'}
          </Typography>
          {client.phoneInvalid && (
            <Box sx={clientsTableStyles.phoneValidation.container}>
              <SvgIcon component={InfoIcon} sx={clientsTableStyles.phoneValidation.icon} />
              <Typography variant="caption" sx={clientsTableStyles.phoneValidation.text}>
                {t('errors:forms.invalidPhone')}
              </Typography>
            </Box>
          )}
        </Box>
      )
    },
    width: '150px',
  },
  {
    Header: CLIENTS_TABLE_DATA_STRUCTURE.barcode.label,
    accessor: CLIENTS_TABLE_DATA_STRUCTURE.barcode.key,
    customRowRender: (_, cell) => {
      const client = cell.row.original
      const isSelected = selectedRows.has(client.id)

      return (
        <Typography
          typography="medium"
          className={CSS_CLASS_NAMES.BARCODE_VALUE}
          sx={
            isSelected
              ? clientsTableStyles.barcode.typographySelected
              : clientsTableStyles.barcode.typography
          }
        >
          {cell.value}
        </Typography>
      )
    },
    width: '120px',
  },
  {
    Header: CLIENTS_TABLE_DATA_STRUCTURE.status.label,
    accessor: CLIENTS_TABLE_DATA_STRUCTURE.status.key,
    customRowRender: (_, cell) => {
      const client = cell.row.original

      return <RetentionBadges membershipStatus={client.membershipStatus} />
    },
    hasSorting: false,
    skeletonShape: TABLE_SKELETON_SHAPES.chip,
    width: '160px',
  },
  {
    Header: CLIENTS_TABLE_DATA_STRUCTURE.createdAt.label,
    accessor: CLIENTS_TABLE_DATA_STRUCTURE.createdAt.key,
    customRowRender: (_, cell) => (
      <Typography typography="medium">
        {siteTimezone === null
          ? ''
          : formatSiteDate(cell.value, 'medium', siteTimezone || undefined) || '—'}
      </Typography>
    ),
    hasSorting: true,
    initialSortDirection: SORTING_DIRECTIONS.desc,
    serverSideKey: CLIENTS_TABLE_DATA_STRUCTURE.createdAt.serverSideKey,
    width: '130px',
  },
]
